/** @format */
import { ChangeDetectionStrategy, Component, DOCUMENT, Inject, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { OtmId, SignatureRequestBasicInfo, UniversitySettings } from 'common-typescript/types';
import { map, Observable, of, shareReplay, switchMap, take } from 'rxjs';
import {
    AppErrorHandler,
    ElectronicSignatureStoredDocumentEntityService,
    GraduationEntityService,
    SignatureRequestBasicInfoEntityService,
    SignatureRequestUtilityService,
    UniversityService,
} from 'sis-components';

/**
 * Lets a student download their own signed electronic degree certificate, once one exists, directly from their attainments.
 */
@Component({
    selector: 'app-degree-certificate-download',
    templateUrl: './degree-certificate-download.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class DegreeCertificateDownloadComponent implements OnInit {
    @Input() degreeProgrammeAttainmentId: OtmId;

    /**
     * Emits the id of the signature request whose signed certificate can be downloaded, or null when none is available
     * (module disabled, no graduation, or no completed signature request). Expected lookup failures (401/404/419) are
     * ignored without emitting, since most attainments simply have no certificate to offer; unexpected errors are still
     * surfaced via {@link AppErrorHandler.defaultErrorHandler}.
     */
    downloadableSignatureRequestId$: Observable<OtmId | null>;

    constructor(
        private readonly graduationEntityService: GraduationEntityService,
        private readonly signatureRequestBasicInfoEntityService: SignatureRequestBasicInfoEntityService,
        private readonly signatureRequestUtilityService: SignatureRequestUtilityService,
        private readonly electronicSignatureStoredDocumentEntityService: ElectronicSignatureStoredDocumentEntityService,
        private readonly universityService: UniversityService,
        private readonly errorHandler: AppErrorHandler,
        @Inject(DOCUMENT) private readonly document: Document,
    ) {}

    ngOnInit(): void {
        this.downloadableSignatureRequestId$ = this.universityService.getCurrentUniversitySettings().pipe(
            switchMap((settings: UniversitySettings | null) =>
                settings?.moduleToggles?.electronicSignatureModuleEnabled
                    ? this.graduationEntityService.getActiveByAttainmentId(this.degreeProgrammeAttainmentId).pipe(
                          // A misregistered graduation's certificate must never be offered for download, even if a signed one exists.
                          switchMap(graduation =>
                              graduation.misregistration
                                  ? of(null)
                                  : this.signatureRequestBasicInfoEntityService.findSignatureRequestBasicInfos(graduation.id),
                          ),
                          map((signatureRequests: SignatureRequestBasicInfo[] | null) =>
                              signatureRequests
                                  ? this.signatureRequestUtilityService.findActiveSignatureRequestForGraduation(signatureRequests)
                                  : null,
                          ),
                          map(signatureRequest =>
                              signatureRequest?.externalSignatureRequestStatus === 'COMPLETE' &&
                              signatureRequest?.fileTransferStatus === 'SAVED'
                                  ? signatureRequest.id
                                  : null,
                          ),
                      )
                    : of(null),
            ),
            this.errorHandler.defaultErrorHandler(),
            shareReplay({ bufferSize: 1, refCount: true }),
        );
    }

    downloadSignedDegreeCertificate(signatureRequestId: OtmId): void {
        this.electronicSignatureStoredDocumentEntityService
            .getElectronicSignatureStoredDocumentDownloadUrl(signatureRequestId)
            .pipe(take(1), this.errorHandler.defaultErrorHandler())
            .subscribe(url => {
                if (url) {
                    this.document.location.href = `/tasku${url}`;
                }
            });
    }
}
